import { FEE_PRODUCTS, type FeeProduct, type FeeTariff } from '@contract';
import { parseAmount } from '@sao/shared/money';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';

import { RulesTable } from '@/components/fees/rules-table';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/primitives/status-pill';
import { ProposeChange, useCanPropose } from '@/components/propose-change';
import { OpenRecord } from '@/components/queue/queue-parts';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { PRODUCT_LABEL, railsFor } from '@/lib/fee-products';
import { quoteSentence, railName } from '@/lib/fee-text';
import { formatDateTime, relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';
import { adminQuery } from '@/lib/query';

const searchSchema = z.object({
  product: z.enum(FEE_PRODUCTS).catch('sao_transfer').default('sao_transfer'),
});

export const Route = createFileRoute('/_app/fees')({
  validateSearch: searchSchema,
  component: FeesPage,
});

const STATE_WORDS: Record<FeeTariff['state'], string> = {
  in_force: 'In force',
  scheduled: 'Scheduled',
  replaced: 'Replaced',
  cancelled: 'Cancelled',
};

const column = createColumnHelper<FeeTariff>();

const TARIFF_COLUMNS = [
  column.accessor('effective_from', {
    header: 'From',
    cell: ({ row, getValue }) => (
      <OpenRecord
        sheetRef={{ type: 'fee', id: row.original.id }}
        label={`Open the tariff from ${formatDateTime(getValue())}`}
      >
        {formatDateTime(getValue())}
      </OpenRecord>
    ),
  }),
  column.accessor('state', {
    header: 'State',
    cell: (info) => (
      <StatusPill
        status={info.getValue()}
        label={STATE_WORDS[info.getValue()]}
      />
    ),
  }),
  column.accessor('rules', {
    header: 'Rules',
    cell: (info) => info.getValue().length,
  }),
  column.accessor('approved_by', {
    header: 'Approved by',
    cell: (info) => info.getValue() ?? '—',
  }),
  column.accessor('note', { header: 'Note', cell: (info) => info.getValue() }),
];

/** Fee tariffs per product: what is in force, what is next, and the past. */
function FeesPage() {
  const { product } = Route.useSearch();
  const list = useQuery(adminQuery('fees.list', {}));
  const sheetActions = useSheetActions();
  const canPropose = useCanPropose('fee_tariff');
  const currency = list.data?.currency ?? homeCurrency();
  const tariffs = list.data?.tariffs.filter((row) => row.product === product);
  const inForce = tariffs?.find((row) => row.state === 'in_force');
  const scheduled = tariffs?.filter((row) => row.state === 'scheduled') ?? [];
  const past = tariffs?.filter(
    (row) => row.state === 'replaced' || row.state === 'cancelled',
  );

  return (
    <>
      <PageHeader
        title="Fees"
        description="Each product's tariff. A new tariff is a change request: someone else approves it, and it takes effect when it says."
        actions={
          canPropose ? (
            <Button
              onClick={() =>
                sheetActions.open({ type: 'fee', id: `new:${product}` })
              }
            >
              Propose new tariff
            </Button>
          ) : null
        }
      />
      <TabLinks
        tabs={FEE_PRODUCTS.map((key) => ({
          key,
          label: PRODUCT_LABEL[key],
        }))}
        active={product}
        render={(tab, domProps) => (
          <Link
            {...domProps}
            to="/fees"
            search={{ product: tab.key as FeeProduct }}
          />
        )}
      />
      {list.isError ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      {tariffs ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>In force</CardTitle>
              <CardDescription>
                {inForce
                  ? `Since ${formatDateTime(inForce.effective_from)}.`
                  : 'No tariff: this product is free.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <RulesTable rules={inForce?.rules ?? []} currency={currency} />
              {inForce ? (
                <div>
                  <OpenRecord
                    sheetRef={{ type: 'fee', id: inForce.id }}
                    label="Open the tariff in force"
                  >
                    Open this tariff
                  </OpenRecord>
                </div>
              ) : null}
            </CardContent>
          </Card>
          <QuotePreview product={product} currency={currency} />
        </div>
      ) : null}
      {scheduled.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Scheduled</CardTitle>
            <CardDescription>
              Approved, and waiting for the day it starts.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {scheduled.map((tariff) => (
              <div
                key={tariff.id}
                className="flex flex-wrap items-center gap-3 text-sm"
              >
                <OpenRecord
                  sheetRef={{ type: 'fee', id: tariff.id }}
                  label={`Open the tariff from ${formatDateTime(tariff.effective_from)}`}
                >
                  Starts {relativeTime(tariff.effective_from)} (
                  {formatDateTime(tariff.effective_from)})
                </OpenRecord>
                <ProposeChange
                  kind="fee_tariff_cancel"
                  payload={{ tariff_id: tariff.id }}
                  label="Propose cancelling"
                  title="Cancel this scheduled tariff"
                  description="It never takes effect; the tariff in force stays."
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
      <section className="flex flex-col gap-2">
        <h2 className="font-semibold">History</h2>
        <DataTable
          id="fee-tariffs"
          columns={TARIFF_COLUMNS}
          data={past}
          empty="No replaced or cancelled tariffs for this product yet."
          rowLabel={(row) =>
            `Open the tariff from ${formatDateTime(row.effective_from)}`
          }
          onRowClick={(row, rowEl) =>
            sheetActions.open(
              { type: 'fee', id: row.id },
              { fromRowEl: rowEl.querySelector('button') ?? rowEl },
            )
          }
        />
      </section>
    </>
  );
}

/** Holds a value back until it has stopped changing for a moment. */
function useSettled<T>(value: T, ms = 400): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** Asks the server what the tariff in force charges for an amount (a read; nothing is sent for approval). */
function QuotePreview({
  product,
  currency,
}: Readonly<{ product: FeeProduct; currency: string }>) {
  const rails = railsFor(product);
  const [amount, setAmount] = useState('250');
  const [rail, setRail] = useState('');
  const activeRail = rails.includes(rail) ? rail : (rails[0] ?? '');
  const amountMinor = parseAmount(amount, currency);
  const args = useSettled(
    amountMinor === null || amountMinor <= 0
      ? null
      : {
          product,
          currency,
          rail: activeRail || undefined,
          amount_minor: amountMinor,
        },
  );
  const quote = useQuery({
    ...adminQuery('fees.preview', args ?? { product, amount_minor: 0 }),
    enabled: args !== null,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Quote preview</CardTitle>
        <CardDescription>
          What the tariff in force charges a customer.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="quote-amount">Amount ({currency})</Label>
            <Input
              id="quote-amount"
              inputMode="decimal"
              className="w-full sm:w-32"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          {rails.length > 0 ? (
            <Select
              label="Rail"
              value={activeRail}
              onChange={(event) => setRail(event.target.value)}
            >
              {rails.map((key) => (
                <option key={key} value={key}>
                  {railName(key)}
                </option>
              ))}
            </Select>
          ) : null}
        </div>
        {quote.isError ? (
          <Alert variant="destructive">{quote.error.message}</Alert>
        ) : null}
        {quote.data && args ? (
          <p className="text-sm" aria-live="polite">
            {quoteSentence(
              product,
              activeRail,
              args.amount_minor,
              quote.data.now,
              currency,
            )}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
